import { RECENTLY_VIEWED_EXPIRATION_DURATION, RECENTLY_VIEWED_LOCAL_STORAGE_KEY, IMAGES } from '@/constants'
import type { ComposedListing, DeliveryMethod, ListingsGroup } from '@/types/app-types'

import type {
    BuildResponsiveSectionImageUrlOptions,
    BuildResponsiveHighlightedMapImageUrlOptions,
    Group,
    GroupMap,
    ListingTicket,
    RecentlyViewedStateLocalStorage,
} from './types'

export function assignGroupMapAttributes(groupMap: GroupMap, composedListing: ComposedListing): void {
    const { bestListingsByQuantity, quantities, cheapestTicketPrice, lowestQuantity } = groupMap
    const { price, quantityOptionsList } = composedListing
    const lowestSplitQuantity = quantityOptionsList[0]

    // in order to find the representative listing for the group (bestListingsByQuantity[0]),
    // we need to find the listing in the group with the lowest price and secondarily the lowest split quantity
    if (
        !!lowestSplitQuantity &&
        (price < cheapestTicketPrice || (price === cheapestTicketPrice && lowestSplitQuantity < lowestQuantity))
    ) {
        groupMap.cheapestTicketPrice = price
        groupMap.lowestQuantity = lowestSplitQuantity
        bestListingsByQuantity[0] = composedListing
    }

    // any quantityOptionsList values that aren't already in quantities
    // should be used to assign the bestListingsByQuantity for that quantity
    quantityOptionsList
        .filter((qty) => !quantities.has(qty))
        .forEach((qty) => {
            if (!bestListingsByQuantity[qty]) {
                bestListingsByQuantity[qty] = composedListing
            }
        })

    // and then be added to quantities
    groupMap.quantities = new Set([...Array.from(quantities), ...quantityOptionsList])
}

export function composeListingsGroups(groupMaps: Record<string, GroupMap>): ListingsGroup[] {
    return Object.keys(groupMaps).map((id) => {
        const group = groupMaps[id]
        const { bestListingsByQuantity, quantities } = group
        const quantityOptionsList = Array.from(quantities).sort((a, b) => a - b)
        const quantityRangeText = getQuantityRangeText(quantityOptionsList)

        return {
            ...bestListingsByQuantity[0],
            id,
            inGroup: false,
            isGroup: true,
            bestListingsByQuantity,
            quantityOptionsList,
            quantityRangeText,
        }
    })
}

// returns inital dummy setup for groupMaps with attributes defined
export function getInitialGroupMaps(groups: Group[] | ListingsGroup[]): Record<string, GroupMap> {
    const groupMaps: Record<string, GroupMap> = {}
    groups.forEach((group) => {
        const groupId = (group as ListingsGroup).id ?? (group as Group).i
        groupMaps[groupId] = {
            id: groupId,
            quantities: new Set([]),
            bestListingsByQuantity: {},
            cheapestTicketPrice: Infinity,
            lowestQuantity: Infinity,
        }
    })

    return groupMaps
}

/** Expects sortedQuantityOptionsList to be sorted in ascending order */
export function getQuantityRangeText(sortedQuantityOptionsList: number[]): string {
    if (sortedQuantityOptionsList.length > 1) {
        const lowQuantity = sortedQuantityOptionsList[0]
        const highQuantity = sortedQuantityOptionsList[sortedQuantityOptionsList.length - 1]

        return `${lowQuantity}–${highQuantity} tickets`
    } else {
        const quantity = sortedQuantityOptionsList[0]
        return quantity === 1 ? `${quantity} ticket` : `${quantity} tickets`
    }
}

export function getQuantityOptionsList(
    splitType: number | undefined,
    quantity: number,
    splitValues: string,
): Array<number> {
    const qtyOptionsList = []

    if (!splitType) {
        if (quantity % 2 === 0 && quantity !== 1) {
            for (let qt = quantity; qt >= 2; qt = qt - 2) {
                qtyOptionsList.push(qt)
            }
        } else {
            qtyOptionsList.push(quantity)
            for (let qt = quantity - 2; qt >= 1; qt--) {
                qtyOptionsList.push(qt)
            }
        }
    } else if (splitType === 3) {
        qtyOptionsList.push(quantity)
        for (let qt = quantity - 2; qt >= 1; qt--) {
            qtyOptionsList.push(qt)
        }
    } else if (splitType === 1) {
        for (let qt = quantity; qt >= 1; qt--) {
            qtyOptionsList.push(qt)
        }
    } else if (splitType === 2) {
        if (splitValues.indexOf(',') < 0) {
            qtyOptionsList.push(parseInt(splitValues))
        } else {
            let temp = []
            temp = splitValues.split(',')
            if (parseInt(temp[0]) < parseInt(temp[1])) {
                for (let qt = temp.length - 1; qt >= 0; qt--) {
                    qtyOptionsList.push(parseInt(temp[qt]))
                }
            } else {
                for (let qt = 0; qt < temp.length; qt++) {
                    qtyOptionsList.push(parseInt(temp[qt]))
                }
            }
        }
    }

    return qtyOptionsList
}

export enum StockType {
    PAPERLESS_WALK_IN = 'Paperless Walk-in',
    PAPERLESS_CARD = 'Paperless Card',
    HARD = 'Hard',
    FLASH = 'Flash',
    TICKETMASTER_TRANSFER = 'Ticketmaster Transfer',
    E_TICKETS = 'E-tickets',
    MOBILE_SCREEN_CAP = 'Mobile Screencap',
}

const DeliveryMethods: Array<DeliveryMethod & { rules: Array<(args: any) => boolean> }> = [
    {
        icon: 'ticket-pick-up',
        name: 'Local Pickup',
        description: 'Pickup your tickets near the venue before your event.',
        rules: [
            (listing: ListingTicket) => listing.stp === StockType.PAPERLESS_WALK_IN && listing.sd === '0',
            (listing: ListingTicket) => listing.stp === StockType.PAPERLESS_CARD && listing.sd === '1',
            (listing: ListingTicket) => listing.stp === StockType.HARD && listing.sd === '1',
        ],
        aipShouldIncludeDelivery: false,
    },
    {
        icon: 'ticket-transfer',
        name: 'Mobile Transfer',
        description: "When your tickets are ready, we'll send a ticket transfer offer to your email.",
        rules: [
            (listing: ListingTicket) => listing.stp === StockType.FLASH && listing.sd === '0',
            (listing: ListingTicket) => listing.stp === StockType.TICKETMASTER_TRANSFER && listing.sd === '0',
        ],
        aipShouldIncludeDelivery: true,
    },
    {
        icon: 'ticket-print',
        name: 'Print at Home',
        description: 'When your tickets are ready, we’ll send a link to download and print your tickets.',
        rules: [(listing: ListingTicket) => listing.stp === StockType.E_TICKETS && listing.sd === '0'],
        aipShouldIncludeDelivery: true,
    },
    {
        icon: 'ticket-truck',
        name: 'Shipped via UPS',
        description: 'Get your tickets delivered to an address of your choice.',
        rules: [
            (listing: ListingTicket) => listing.stp === StockType.HARD && listing.sd === '0',
            (listing: ListingTicket) => listing.stp === StockType.PAPERLESS_CARD && listing.sd === '0',
        ],
        aipShouldIncludeDelivery: false,
    },
    {
        icon: 'ticket-mobile',
        name: 'Straight to Your Pocket',
        description: 'Get your tickets in the Vivid Seats app or through your account on a mobile device.',
        rules: [(listing: ListingTicket) => listing.stp === StockType.MOBILE_SCREEN_CAP && listing.sd === '0'],
        aipShouldIncludeDelivery: false,
    },
]

export const retrieveDeliveryMethodByListing = (listing: ListingTicket): DeliveryMethod | undefined => {
    for (const deliveryMethod of DeliveryMethods) {
        if (deliveryMethod.rules.find((rule) => rule(listing))) {
            return {
                icon: deliveryMethod.icon,
                name: deliveryMethod.name,
                description: deliveryMethod.description,
                aipShouldIncludeDelivery: deliveryMethod.aipShouldIncludeDelivery,
            }
        }
    }
    return undefined
}

export const getRecentlyViewedLocalStorage = (): RecentlyViewedStateLocalStorage => {
    const rawStored = localStorage ? localStorage.getItem(RECENTLY_VIEWED_LOCAL_STORAGE_KEY) ?? '{}' : '{}'
    const result = JSON.parse(rawStored)

    return result
}

export const setRecentlyViewedLocalStorage = (payload: RecentlyViewedStateLocalStorage) => {
    try {
        localStorage.setItem(RECENTLY_VIEWED_LOCAL_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
        console.log('Invalid Local Storage value, recently viewed not parsed at this time', e)
    }
}

export const setRecentlyViewedLocalStorageById = (listingId: string, productionId: number) => {
    const currentStoredRecentlyViewed = getRecentlyViewedLocalStorage()
    const stringProductionId = productionId.toString()
    const productionRecentlyViewedStored = currentStoredRecentlyViewed[stringProductionId]

    const date = new Date()
    date.setDate(date.getDate() + RECENTLY_VIEWED_EXPIRATION_DURATION)
    const expireIn = date.toUTCString()

    let productionRecentlyViewedToUpdate
    if (productionRecentlyViewedStored) {
        productionRecentlyViewedStored.viewedListings[listingId] = true
        productionRecentlyViewedToUpdate = {
            [stringProductionId]: {
                ...productionRecentlyViewedStored,
                expireIn,
            },
        }
    } else {
        productionRecentlyViewedToUpdate = {
            [stringProductionId]: {
                viewedListings: { [listingId]: true },
                expireIn,
            },
        }
    }

    const resultRecentlyViewedToBeStored: RecentlyViewedStateLocalStorage = {
        ...currentStoredRecentlyViewed,
        ...productionRecentlyViewedToUpdate,
    }

    setRecentlyViewedLocalStorage(resultRecentlyViewedToBeStored)
}

export const removeExpiredRecentlyViewedFromLocalStorage = () => {
    const nowDate = new Date()

    const recentlyViewedStored = getRecentlyViewedLocalStorage()

    Object.entries(recentlyViewedStored).forEach(([key, value]) => {
        if (new Date(value.expireIn) < nowDate) {
            delete recentlyViewedStored[key]
        }
    })

    setRecentlyViewedLocalStorage(recentlyViewedStored)
}

export const buildResponsiveSectionImageUrls = (options: BuildResponsiveSectionImageUrlOptions) => {
    const { path, height, width } = options
    if (!path) return ''
    const heightPath = height && `h_${height}`
    const widthPath = width && `w_${width}`
    const sizeProps = [heightPath, widthPath].filter(Boolean).join()

    return `https:${process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX}${
        sizeProps ? `/${sizeProps}` : ''
    }/hero/seat-view${path}`
}

export const buildResponsiveHighlightedMapImageUrls = (options: BuildResponsiveHighlightedMapImageUrlOptions) => {
    const { layoutId, sectionId, height, width } = options
    if (!layoutId || !sectionId) return ''
    if (sectionId === '0') return IMAGES.MAPS_NO_IMAGE_AVAILIBLE
    const heightPath = height && `h_${height}`
    const widthPath = width && `w_${width}`
    const sizeProps = [heightPath, widthPath].filter(Boolean).join()

    return `https:${process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX}${
        sizeProps ? `/${sizeProps},c_fit` : ''
    }/hero/section-highlight-images/${layoutId}/${sectionId}.png`
}
